import { ECreditApplicationStatus } from '@if/core/types'
import * as yup from '@if/core/yup'
import { type FormMethods, TranslatedError, useFormDialog } from '@if/mui'
import { Box } from '@mui/material'
import { usePostStatus } from '@src/data/api/credit-api/credit-api'
import { fetchCancelReasons, useSubmitFeedback } from '@src/data/api/feedback-api/feedback-api'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { ValueListItem } from '@src/types/ValueListDto'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useCallback } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FeedbackOption from '../FeedBackOption'
import buildSelectedWithdrawalReasonFeedbackDto from '../feedback-hooks'

const cancelSchema = yup.object({
  selectedReason: yup.text().required(),
})

type CancelFormValues = yup.InferSubmit<typeof cancelSchema>

const isCancelNotAllowedLoanExists = (error: unknown): boolean => {
  const response = (error as AxiosError)?.response
  return response?.status === 400 && response?.data === 'CancelNotAllowedLoanExists'
}

const CancelReasonsContent = ({
  reasons,
  form,
}: {
  reasons: ValueListItem[]
  form: FormMethods<CancelFormValues>
}) => {
  const selectedReason = useWatch({ control: form.control, name: 'selectedReason' })

  return (
    <Box sx={{ minWidth: 320 }}>
      {reasons.map((r) => (
        <FeedbackOption
          key={r.id}
          id={r.id}
          text={r.text}
          selected={selectedReason}
          onChange={(value) => form.setValue('selectedReason', value, { shouldValidate: true, shouldDirty: true })}
        />
      ))}
    </Box>
  )
}

export const useCancelReasonsDialog = (creditApp: CreditApplication) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation()
  const queryClient = useQueryClient()
  const { mutateAsync: postCancelApp } = usePostStatus()
  const [submitFeedback] = useSubmitFeedback()

  const dialog = useFormDialog<CancelFormValues, ValueListItem[], void>({
    title: t('withdrawalReasonDialog.title'),
    confirmLabel: t('viewCreditApplication.cancelApplication'),
    successMessage: t('viewCreditApplication.creditAppCancelled'),
    schema: cancelSchema,
    defaultValues: { selectedReason: '' },
    content: ({ props, form }) => <CancelReasonsContent reasons={props} form={form} />,
    onSubmit: async ({ selectedReason }) => {
      try {
        await postCancelApp({
          id: creditApp.id,
          status: ECreditApplicationStatus.Cancelled,
          financingProgramId: creditApp.financingProgramId,
        })
      } catch (error) {
        if (isCancelNotAllowedLoanExists(error)) throw new TranslatedError('common.errors.CancelNotAllowedLoanExists')
        throw error
      }
      await submitFeedback(
        buildSelectedWithdrawalReasonFeedbackDto(selectedReason, creditApp.id, creditApp.financingProgramId, false),
      )
    },
  })

  const open = useCallback(
    () => dialog.create(() => fetchCancelReasons(queryClient, resolvedLanguage ?? 'en')),
    [dialog, queryClient, resolvedLanguage],
  )

  return { open }
}
